import React, { useCallback, useEffect, useRef, useState } from 'react';
import { backupReportsApi } from '../../services/backupReportsApi';

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const fieldClass = 'w-full rounded-xl border border-white/20 bg-[#140728] p-3 text-sm text-white';
const panelClass = 'rounded-2xl border border-white/10 bg-white/5 p-5 mb-5';
const buttonClass = 'rounded-xl border border-violet-400/40 bg-violet-500/20 px-4 py-2 text-sm disabled:opacity-50';
const errorMessage = (error) => {
  const data = error.response?.data;
  if (typeof data?.message === 'string') return data.message;
  if (typeof data?.detail === 'string') return data.detail;
  if (Array.isArray(data?.detail)) {
    const messages = data.detail.map((item) => item.msg).filter((message) => typeof message === 'string');
    if (messages.length) return messages.join('; ');
  }
  return error.message || 'No se pudo conectar con el servidor.';
};
const formatDate = (value) => value ? new Date(value).toLocaleString('es-MX', { timeZone: 'UTC' }) : 'No informado';

export default function Respaldos() {
  const today = new Date();
  const [groups, setGroups] = useState([]);
  const [optionsError, setOptionsError] = useState('');
  const [filters, setFilters] = useState({ cliente: '', anio: today.getFullYear(), mes: today.getMonth() + 1, periodo: 'mensual' });
  const [result, setResult] = useState(null);
  const [queryError, setQueryError] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyResult, setHistoryResult] = useState(null);
  const [historyError, setHistoryError] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sheet, setSheet] = useState('diario');
  const [request, setRequest] = useState(null);
  const [generation, setGeneration] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState('');
  const [checking, setChecking] = useState(false);
  const [optionLoading, setOptionLoading] = useState(false);
  const version = useRef(0);
  const historyVersion = useRef(0);
  const mounted = useRef(false);

  const loadOptions = useCallback(async () => {
    setOptionLoading(true);
    setOptionsError('');
    try {
      const response = await backupReportsApi.options();
      if (mounted.current) setGroups(response.data.grupos);
    } catch (error) {
      if (mounted.current) setOptionsError(errorMessage(error));
    } finally {
      if (mounted.current) setOptionLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    loadOptions();
    return () => { mounted.current = false; version.current += 1; historyVersion.current += 1; };
  }, [loadOptions]);

  const query = async (selected, page = 1) => {
    const current = ++version.current;
    setLoading(true);
    setQueryError('');
    try {
      const response = await backupReportsApi.query(selected, page);
      if (mounted.current && current === version.current) setResult({ ...response.data, filters: selected });
    } catch (error) {
      if (mounted.current && current === version.current) {
        const data = error.response?.data;
        setResult(data?.cliente ? { ...data, filters: selected } : null);
        setQueryError(data?.catalog_error || data?.preview_error ? '' : errorMessage(error));
      }
    } finally {
      if (mounted.current && current === version.current) setLoading(false);
    }
  };

  const queryHistory = async () => {
    if (filters.cliente !== 'Fonacot') return;
    const current = ++historyVersion.current;
    const selected = { ...filters };
    setHistoryLoading(true);
    setHistoryError('');
    setHistoryResult(null);
    try {
      const response = await backupReportsApi.history(selected);
      if (mounted.current && current === historyVersion.current) {
        setHistoryResult({ ...response.data, filters: selected });
      }
    } catch (error) {
      if (mounted.current && current === historyVersion.current) setHistoryError(errorMessage(error));
    } finally {
      if (mounted.current && current === historyVersion.current) setHistoryLoading(false);
    }
  };

  const changeFilter = (key, value) => {
    version.current += 1;
    historyVersion.current += 1;
    setFilters((previous) => ({ ...previous, [key]: value }));
    setResult(null);
    setQueryError('');
    setLoading(false);
    setHistoryResult(null);
    setHistoryError('');
    setHistoryLoading(false);
  };

  const policyEnabled = groups.some((group) => group.proveedor === 'aws'
    && group.clientes.some((customer) => customer.value === filters.cliente && customer.politicas_aws));

  const generate = async (policies = false) => {
    const source = filters.cliente === 'Fonacot' ? historyResult : result;
    if (generating || (policies ? !policyEnabled : !source)) return;
    const selected = { ...(policies ? filters : source.filters) };
    setGenerating(true);
    setGenerationError('');
    setGeneration(null);
    setRequest(null);
    try {
      const response = policies
        ? await backupReportsApi.policies(selected.cliente)
        : await backupReportsApi.generate(selected);
      if (mounted.current) {
        setRequest({ token: response.data.request_token, filters: selected });
        setGeneration(response.data);
      }
    } catch (error) {
      if (mounted.current) {
        setGenerationError(errorMessage(error));
        setGenerating(false);
      }
    }
  };

  const checkStatus = async () => {
    if (!request) return;
    setChecking(true);
    setGenerationError('');
    try {
      const response = await backupReportsApi.status(request.token);
      if (!mounted.current) return;
      setGeneration(response.data);
      if (response.data.state !== 'PENDING') setGenerating(false);
    } catch (error) {
      if (mounted.current) setGenerationError(errorMessage(error));
    } finally {
      if (mounted.current) setChecking(false);
    }
  };

  useEffect(() => {
    if (!request || !generating || generationError) return undefined;
    let cancelled = false;
    let timer;
    let attempts = 0;
    const poll = async () => {
      try {
        const response = await backupReportsApi.status(request.token);
        if (cancelled) return;
        setGeneration(response.data);
        if (response.data.state === 'PENDING') {
          attempts += 1;
          if (attempts < 180) timer = setTimeout(poll, 10000);
          else setGenerationError('La generación sigue pendiente. Usa Consultar estado para continuar.');
        } else {
          setGenerating(false);
        }
      } catch (error) {
        if (!cancelled) setGenerationError(errorMessage(error));
      }
    };
    timer = setTimeout(poll, 2000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [request, generating, generationError]);

  const preview = historyResult?.preview;
  const currentSheet = preview?.sheets?.find((item) => item.key === sheet) || preview?.sheets?.[0];
  const download = (file) => file.url
    ? <a href={file.url} rel="noreferrer" className="text-green-300 underline">Descargar</a>
    : 'No disponible';

  return (
    <div>
      <section className={panelClass}>
        <h2 className="text-xl font-semibold mb-3">Respaldos AWS y Azure</h2>
        <p className="text-sm text-violet-200/70 mb-4">Consultar archivos lista documentos guardados en GCS. AWS consulta políticas actuales; solo Fonacot permite consultar el histórico de respaldos. Azure consulta el estado actual al generar; el periodo solo filtra jobs.</p>
        {optionsError && <div role="alert" className="text-red-300 mb-3">{optionsError} <button onClick={loadOptions} disabled={optionLoading} className={buttonClass}>Reintentar opciones</button></div>}
        <form onSubmit={(event) => { event.preventDefault(); setResult(null); query({ ...filters }); }}>
          <div className="grid gap-4 md:grid-cols-4">
            <label>Cliente / nube<select required value={filters.cliente} onChange={(event) => changeFilter('cliente', event.target.value)} className={fieldClass} disabled={optionLoading}>
              <option value="">Seleccionar cliente</option>
              {groups.map((group) => <optgroup key={group.proveedor} label={group.nombre}>{group.clientes.map((customer) => <option key={customer.value} value={customer.value}>{customer.nombre}</option>)}</optgroup>)}
            </select></label>
            <label>Año<input required type="number" min="2000" max="2100" value={filters.anio} onChange={(event) => changeFilter('anio', Number(event.target.value))} className={fieldClass} /></label>
            <label>Mes<select value={filters.mes} onChange={(event) => changeFilter('mes', Number(event.target.value))} className={fieldClass}>{MONTHS.map((month, index) => <option key={month} value={index + 1}>{month}</option>)}</select></label>
            <label>Periodo<select value={filters.periodo} onChange={(event) => changeFilter('periodo', event.target.value)} className={fieldClass}>
              <option value="mensual">Mensual (mes completo)</option><option value="primera">Primera quincena (1–15)</option><option value="segunda">Segunda quincena (16–fin de mes)</option>
            </select></label>
          </div>
          <div className="flex flex-wrap gap-3 mt-5">
            <button type="submit" disabled={loading || optionLoading || !filters.cliente} className={buttonClass}>{loading ? 'Consultando archivos…' : 'Consultar archivos'}</button>
            {policyEnabled && <button type="button" onClick={() => generate(true)} disabled={generating || optionLoading || loading} className={buttonClass}>Consultar políticas</button>}
            {filters.cliente === 'Fonacot' && <button type="button" onClick={queryHistory} disabled={historyLoading || optionLoading} className={buttonClass}>{historyLoading ? 'Consultando respaldos…' : 'Consultar respaldos'}</button>}
            {(historyResult || (result?.proveedor === 'azure')) && <button type="button" onClick={() => generate()} disabled={generating || loading || historyLoading || (filters.cliente === 'Fonacot' && !preview?.total)} className={buttonClass}>
              {generating ? 'Generación en curso…' : filters.cliente === 'Fonacot' ? 'Generar Excel AWS en GCS' : 'Generar Word + Excel de jobs Azure en GCS'}
            </button>}
          </div>
          <p className="text-xs text-violet-200/60 mt-3">Fechas del reporte en America/Mexico_City. Consultar no genera documentos ni recolecta datos Azure.</p>
          {policyEnabled && <p className="text-xs text-violet-200/60 mt-2">Consultar políticas consulta la configuración actual de AWS Backup de todas las cuentas configuradas del cliente, no el periodo seleccionado. Genera un Word de solo lectura y lo guarda en GCS con la fecha de consulta. Para encontrarlo en el catálogo, selecciona el mes y periodo que contengan esa fecha.</p>}
        </form>
        {queryError && <p role="alert" className="text-red-300 mt-3">{queryError}</p>}
        {generationError && <p role="alert" className="text-red-300 mt-3">{generationError}</p>}
        {historyError && <p role="alert" className="text-red-300 mt-3">{historyError}</p>}
      </section>

      {request && <section className={panelClass} aria-live="polite">
        <h3 className="font-semibold mb-2">Seguimiento de generación · {request.filters.cliente}</h3>
        <p>{generation?.message}</p>
        {!!generation?.files?.length && <div className="overflow-x-auto mt-3"><table className="w-full text-sm text-left">
          <thead><tr><th className="p-3">Reporte</th><th>Periodo</th><th>Estado</th><th>Descarga</th></tr></thead>
          <tbody>{generation.files.map((file, index) => <tr key={index} className="border-t border-white/10"><td className="p-3">{file.label}</td><td>{file.periodo}</td><td>{file.state === 'READY' ? 'Listo' : file.message}</td><td>{download(file)}</td></tr>)}</tbody>
        </table></div>}
        <button onClick={checkStatus} disabled={checking} className={`${buttonClass} mt-3`}>Consultar estado / renovar enlaces</button>
        <p className="text-xs text-violet-200/60 mt-2">Al terminar, pulsa Consultar archivos para actualizar el catálogo.</p>
      </section>}

      {result && <section className={panelClass}>
        <h3 className="font-semibold mb-2">Archivos generados en GCS · {result.cliente}</h3>
        <p className="text-xs text-violet-200/60 mb-3">El mensual incluye ambas quincenas y otros rangos contenidos en el mes. Cada archivo conserva su periodo real. Generados desde portal, agente o comando.</p>
        {result.catalog_error ? <p role="alert" className="text-red-300">{result.catalog_error}</p> : <>
          <div className="overflow-x-auto"><table className="w-full text-left text-sm">
            <thead><tr><th className="p-3">Reporte</th><th>Periodo</th><th>Generado (UTC)</th><th>Origen</th><th>Estado</th><th>Descarga</th></tr></thead>
            <tbody>{result.files?.length ? result.files.map((file) => <tr key={file.blob} className="border-t border-white/10">
              <td className="p-3">{file.label}<p className="text-xs opacity-60">{file.filename}</p></td><td className="pr-3 whitespace-nowrap">{file.inicio} al {file.fin}</td>
              <td className="pr-3">{formatDate(file.created)}</td><td>{file.origen}</td><td>{file.error || 'Listo'}</td><td>{download(file)}</td>
            </tr>) : <tr><td colSpan="6" className="p-3">No hay archivos generados para este periodo.</td></tr>}</tbody>
          </table></div>
          {result.pages > 1 && <nav aria-label="Páginas del catálogo" className="flex items-center gap-4 mt-3">
            <button disabled={loading || result.page <= 1} onClick={() => query(result.filters, result.page - 1)} className={buttonClass}>Anterior</button>
            <span>Página {result.page} de {result.pages} · {result.total_files} archivos</span>
            <button disabled={loading || result.page >= result.pages} onClick={() => query(result.filters, result.page + 1)} className={buttonClass}>Siguiente</button>
          </nav>}
        </>}
      </section>}

      {preview && <section aria-label="Vista previa AWS" className={panelClass}>
        <h3 className="font-semibold">Vista previa del Excel · Fonacot · AWS</h3>
        <p className="text-sm opacity-70">{preview.total} registros del periodo</p>
        <div className="flex gap-3 my-4">{preview.sheets.map((item) => <button key={item.key} onClick={() => setSheet(item.key)} aria-pressed={currentSheet?.key === item.key} className={buttonClass}>{item.label}</button>)}</div>
        {!preview.total && <p>No se cuenta con respaldos registrados en estas fechas. Esto no demuestra que no hayan existido.</p>}
        {!!preview.excluded && <p className="text-amber-200">{preview.excluded} registros quedan fuera de las tres hojas.</p>}
        {currentSheet?.blocks.map((block) => <div key={block.title} className="mb-5">
          <h4 className="font-semibold">{block.title}</h4><p className="text-xs opacity-70 mb-2">{block.region} · {currentSheet.label} · {block.count} registros</p>
          {block.truncated && <p className="text-xs text-amber-200 mb-2">Vista previa: los {preview.preview_limit} más recientes. El Excel incluye los {block.count} registros.</p>}
          <div className="overflow-x-auto"><table className="w-full text-left text-xs whitespace-nowrap">
            <thead><tr>{block.headers.map((header) => <th key={header} className="p-3 bg-white/5">{header}</th>)}</tr></thead>
            <tbody>{block.rows.length ? block.rows.map((row, index) => <tr key={index} className="border-t border-white/10">{row.map((value, cell) => <td key={cell} className="p-3">{value}</td>)}</tr>) : <tr><td colSpan={block.headers.length} className="p-3">No se cuenta con respaldos registrados en estas fechas.</td></tr>}</tbody>
          </table></div>
        </div>)}
        <p className="text-xs opacity-60">Histórico observado, no garantía de restauración. No recupera respaldos eliminados antes de la primera sincronización.</p>
      </section>}
    </div>
  );
}
